import { forwardRef } from "react";
import { motion } from "framer-motion";
import {
  FaMapMarkerAlt,
  FaPlane,
  FaCalendarAlt,
  FaExchangeAlt,
  FaSearch,
  FaSpinner,
  FaChevronDown,
  FaUsers,
} from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";

const SearchForm = ({
  formData,
  setFormData,
  tripType,
  setTripType,
  airports,
  airportsByServiceType,
  filteredFromAirports,
  setFilteredFromAirports,
  filteredToAirports,
  setFilteredToAirports,
  dropdowns,
  setDropdowns,
  isLoading,
  handleSearch,
  handleFromInputChange,
  handleToInputChange,
  handleFromFocus,
  handleToFocus,
  toggleDropdown,
  selectOption,
  swapLocations,
  handleInputChange,
  getTodayDate,
  getMinReturnDate,
  travellersRef,
  fromRef,
  toRef,
  serviceRef,
  fareRef,
  isLoadingLocations,
  isLoadingFromAirports,
  isLoadingToAirports,
}) => {
  const { setWaiting } = useAuth();

  return (
    <motion.div
      className="w-full max-w-5xl mx-auto rounded-2xl overflow-hidden shadow-xl shadow-slate-300/40 border border-white/20 bg-white/98 backdrop-blur-sm"
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
    >
      <div className="p-5 sm:p-6 lg:p-8">
        {/* Trip type + Fare & Travellers row */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 pb-6 border-b border-slate-200/80">
          <TripTypeTabs tripType={tripType} setTripType={setTripType} />
          <ServiceFareTravellers
            formData={formData}
            setFormData={setFormData}
            dropdowns={dropdowns}
            setDropdowns={setDropdowns}
            travellersRef={travellersRef}
            serviceRef={serviceRef}
            fareRef={fareRef}
            toggleDropdown={toggleDropdown}
          />
        </div>

        <form onSubmit={handleSearch} className="space-y-4 sm:space-y-5">
          {/* From / To row - responsive grid */}
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 sm:gap-4 items-end">
            <LocationInputs
            formData={formData}
            fromRef={fromRef}
            toRef={toRef}
            dropdowns={dropdowns}
            setDropdowns={setDropdowns}
            filteredFromAirports={filteredFromAirports}
            filteredToAirports={filteredToAirports}
            handleFromInputChange={handleFromInputChange}
            handleToInputChange={handleToInputChange}
            handleFromFocus={handleFromFocus}
            handleToFocus={handleToFocus}
            selectOption={selectOption}
            swapLocations={swapLocations}
            isLoadingLocations={isLoadingLocations}
            isLoadingFromAirports={isLoadingFromAirports}
            isLoadingToAirports={isLoadingToAirports}
          />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 items-end">
            <DateInputs
              formData={formData}
              tripType={tripType}
              handleInputChange={handleInputChange}
              getTodayDate={getTodayDate}
              getMinReturnDate={getMinReturnDate}
            />
          </div>

          <SearchButton
            isLoading={isLoading}
            setWaiting={() => setWaiting(true)}
          />
        </form>
      </div>
    </motion.div>
  );
};

const TripTypeTabs = ({ tripType, setTripType }) => (
  <div className="flex flex-wrap sm:flex-nowrap gap-2">
    <span className="w-full sm:w-auto text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1 sm:mb-0 sm:mr-2 sm:self-center">Trip</span>
    {[
      { id: 0, label: "One Way" },
      { id: 1, label: "Round Trip" },
    ].map((tab) => (
      <motion.button
        key={tab.id}
        type="button"
        onClick={() => setTripType(tab?.id)}
        className={`min-h-[44px] px-5 py-2.5 rounded-xl font-semibold transition-all duration-200 text-sm sm:text-base ${
          tripType === tab.id
            ? "bg-blue-600 text-white shadow-md shadow-blue-500/25"
            : "bg-slate-100 text-slate-600 hover:bg-slate-200 border border-transparent"
        }`}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        {tab.label}
      </motion.button>
    ))}
  </div>
);

const ServiceFareTravellers = ({
  formData,
  setFormData,
  dropdowns,
  setDropdowns,
  travellersRef,
  serviceRef,
  fareRef,
  toggleDropdown,
}) => (
  <div className="flex flex-col sm:flex-row flex-wrap items-start sm:items-end gap-4">
    {/* Service Type hidden: auto-derived from From/To airport (India = Domestic, else International) */}
    <div className="hidden" aria-hidden="true">
      <DropdownSelect
        ref={serviceRef}
        label="Service Type"
        value={formData.serType === 1 ? "Domestic" : "International"}
        isOpen={dropdowns.service}
        onToggle={() => toggleDropdown("service")}
        options={[
          { value: 1, label: "Domestic", description: "" },
          { value: 2, label: "International", description: "" },
        ]}
        onSelect={(value) => setFormData((prev) => ({ ...prev, serType: value }))}
      />
    </div>

    <DropdownSelect
      ref={fareRef}
      label="Fare Type"
      value={
        formData.fareType === "A"
          ? "All"
          : formData.fareType === "S"
          ? "Student"
          : formData.fareType === "C"
          ? "Senior Citizen"
          : "Defence"
      }
      isOpen={dropdowns.fare}
      onToggle={() => toggleDropdown("fare")}
      options={[
        {
          value: "A",
          label: "All",
          description: "Regular fares",
        },
        {
          value: "S",
          label: "Student",
          description: "Students discounts",
        },
        {
          value: "C",
          label: "Senior Citizen",
          description: "Senior citizen benefits",
        },
        {
          value: "D",
          label: "Defence",
          description: "Defence personal rate",
        },
      ]}
      onSelect={(value) =>
        setFormData((prev) => ({ ...prev, fareType: value }))
      }
    />

    <TravellersDropdown
      ref={travellersRef}
      formData={formData}
      setFormData={setFormData}
      isOpen={dropdowns.travellers}
      onToggle={() => toggleDropdown("travellers")}
    />
  </div>
);

const DropdownSelect = forwardRef(
  ({ label, value, isOpen, onToggle, options, onSelect }, ref) => (
    <motion.div
      className="relative flex-1 w-full min-w-0"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.7 }}
      ref={ref}
    >
      <label className="block text-sm font-semibold text-gray-700 mb-2">
        {label}
      </label>
      <div className="relative">
        <button
          type="button"
          onClick={onToggle}
          className="w-full pl-4 pr-10 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400 transition-all duration-200 bg-white text-gray-800 text-left cursor-pointer hover:border-slate-300 hover:bg-slate-50/50"
        >
          {value}
        </button>
        <FaChevronDown className={`absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none transition-transform ${isOpen ? "rotate-180" : ""}`} />

        {isOpen && (
          <div className="absolute top-full left-0 right-0 mt-1.5 bg-white border border-slate-200 rounded-xl shadow-xl z-50 max-h-60 overflow-y-auto ring-1 ring-slate-200/50">
            {options.map((option, index) => (
              <button
                type="button"
                key={index}
                className="w-full text-left px-4 py-3 hover:bg-slate-50 active:bg-slate-100 cursor-pointer text-sm text-gray-700 border-b border-slate-100 last:border-b-0 transition-colors"
                onClick={() => {
                  onSelect(option.value);
                  onToggle();
                }}
              >
                <div className="font-medium text-slate-800">{option.label}</div>
                {option.description && (
                  <div className="text-xs text-slate-500 mt-0.5">
                    {option.description}
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  )
);

const TravellersDropdown = forwardRef(
  ({ formData, setFormData, isOpen, onToggle }, ref) => (
    <motion.div
      ref={ref}
      className="relative flex-[2] w-full min-w-0"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.9 }}
    >
      <label className="block text-sm font-semibold text-gray-700 mb-2">
        Travellers & Class
      </label>
      <div className="relative">
        <button
          type="button"
          onClick={onToggle}
          className="w-full flex items-center justify-between px-4 py-2.5 min-h-[44px] border border-slate-200 rounded-xl bg-white text-gray-800 cursor-pointer hover:bg-slate-50/50 hover:border-slate-300 focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400 transition-all duration-200"
        >
          <span className="text-sm whitespace-nowrap truncate pr-2">
            {formData.adults} Adult{formData.adults > 1 ? "s" : ""},{" "}
            {formData.children} Child{formData.children > 1 ? "ren" : ""},{" "}
            {formData.infants} Infant{formData.infants > 1 ? "s" : ""} •{" "}
            {formData.cabin === "E"
              ? "Economy"
              : formData.cabin === "P"
              ? "Premium Economy"
              : formData.cabin === "B"
              ? "Business"
              : "First Class"}
          </span>
          <FaChevronDown
            className={`flex-shrink-0 text-slate-400 text-sm transition-transform duration-200 ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </button>

        {isOpen && (
          <TravellersOptions formData={formData} setFormData={setFormData} />
        )}
      </div>
    </motion.div>
  )
);

const TravellersOptions = ({ formData, setFormData }) => (
  <div className="absolute top-full left-0 right-0 sm:left-auto sm:right-0 mt-1.5 bg-white border border-slate-200 rounded-xl shadow-xl z-50 p-4 w-full sm:w-auto sm:min-w-[300px] max-w-sm">
    <PassengerSection
      title="ADULTS (12y +)"
      count={formData.adults}
      min={1}
      max={9}
      onChange={(newAdults) => {
        const newChildren = Math.min(formData.children, 9 - newAdults);
        const newInfants = Math.min(formData.infants, newAdults, 4);
        setFormData((v) => ({
          ...v,
          adults: newAdults,
          children: newChildren,
          infants: newInfants,
        }));
      }}
    />

    <PassengerSection
      title="CHILDREN (2y - 12y)"
      count={formData.children}
      min={0}
      max={Math.max(0, 9 - formData.adults)}
      onChange={(newChildren) => {
        setFormData((v) => ({
          ...v,
          children: newChildren,
        }));
      }}
    />

    <PassengerSection
      title="INFANTS (below 2y)"
      count={formData.infants}
      min={0}
      max={Math.min(formData.adults, 4)}
      onChange={(newInfants) => {
        setFormData((v) => ({
          ...v,
          infants: newInfants,
        }));
      }}
    />

    <CabinClassSection formData={formData} setFormData={setFormData} />
  </div>
);

const PassengerSection = ({ title, count, min = 0, max, onChange }) => (
  <div className="mb-4">
    <h4 className="text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide">{title}</h4>
    <div className="flex flex-wrap gap-2">
      {Array.from({ length: Math.max(0, max - min + 1) }, (_, i) => min + i).map((num) => (
        <button
          key={num}
          type="button"
          onClick={() => onChange(num)}
          className={`min-w-[36px] min-h-[36px] px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${
            count === num
              ? "bg-slate-800 text-white shadow-sm"
              : "bg-slate-100 text-slate-600 hover:bg-slate-200"
          }`}
        >
          {num}
        </button>
      ))}
    </div>
  </div>
);

const CabinClassSection = ({ formData, setFormData }) => (
  <div>
    <h4 className="text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide">Travel Class</h4>
    <div className="flex flex-wrap gap-2">
      {[
        { value: "E", label: "Economy" },
        { value: "P", label: "Premium" },
        { value: "B", label: "Business" },
        { value: "F", label: "First" },
      ].map((cabin) => (
        <button
          key={cabin.value}
          type="button"
          onClick={() => setFormData((v) => ({ ...v, cabin: cabin.value }))}
          className={`min-h-[36px] px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${
            formData.cabin === cabin.value
              ? "bg-blue-600 text-white shadow-sm"
              : "bg-slate-100 text-slate-600 hover:bg-slate-200"
          }`}
        >
          {cabin.label}
        </button>
      ))}
    </div>
  </div>
);

const LocationInputs = ({
  formData,
  fromRef,
  toRef,
  dropdowns,
  setDropdowns,
  filteredFromAirports,
  filteredToAirports,
  handleFromInputChange,
  handleToInputChange,
  handleFromFocus,
  handleToFocus,
  selectOption,
  swapLocations,
  isLoadingLocations,
  isLoadingFromAirports,
  isLoadingToAirports,
}) => (
  <>
    <LocationInput
      ref={fromRef}
      dropdownKey="from"
      label="From"
      value={formData.from}
      onChange={handleFromInputChange}
      isOpen={dropdowns.from}
      onFocus={handleFromFocus}
      filteredAirports={filteredFromAirports}
      onSelect={(value) => selectOption("from", value)}
      icon={FaMapMarkerAlt}
      placeholder="City, airport or code"
      isLoadingLocations={isLoadingLocations}
      setDropdowns={setDropdowns}
      isLoadingAirports={isLoadingFromAirports}
    />

    <SwapButton onSwap={swapLocations} />

    <LocationInput
      ref={toRef}
      dropdownKey="to"
      label="To"
      value={formData.to}
      onChange={handleToInputChange}
      isOpen={dropdowns.to}
      onFocus={handleToFocus}
      filteredAirports={filteredToAirports}
      onSelect={(value) => selectOption("to", value)}
      icon={FaPlane}
      placeholder="City, airport or code"
      isLoadingLocations={isLoadingLocations}
      setDropdowns={setDropdowns}
      isLoadingAirports={isLoadingToAirports}
    />
  </>
);

const LocationInput = forwardRef(
  (
    {
      dropdownKey,
      label,
      value,
      onChange,
      isOpen,
      onFocus,
      filteredAirports,
      onSelect,
      icon: Icon,
      placeholder,
      isLoadingLocations,
      setDropdowns,
      isLoadingAirports,
    },
    ref
  ) => (
    <motion.div
      className="relative flex-1 w-full min-w-0"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.7 }}
      ref={ref}
    >
      <label className="block text-sm font-semibold text-gray-700 mb-2">
        {label} <span className="text-red-500">*</span>
      </label>
      <div className="relative">
        <Icon className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 text-lg pointer-events-none" />
        <input
          type="text"
          value={value}
          onChange={onChange}
          onFocus={onFocus}
          placeholder={placeholder}
          className="w-full pl-11 pr-10 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400 transition-all duration-200 bg-white text-gray-800 placeholder:text-slate-400 hover:border-slate-300"
          required
        />
        <FaChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none" />

        {isOpen && (
          <LocationDropdown
            airports={filteredAirports}
            onSelect={onSelect}
            onClose={() => setDropdowns((prev) => ({ ...prev, [dropdownKey]: false }))}
            isLoadingLocations={isLoadingLocations}
            isLoadingAirports={isLoadingAirports}
          />
        )}
      </div>
    </motion.div>
  )
);

const LocationDropdown = ({
  airports,
  onSelect,
  onClose,
  isLoadingLocations,
  isLoadingAirports,
}) => (
  <div className="absolute top-full left-0 right-0 mt-1.5 bg-white border border-slate-200 rounded-xl shadow-xl z-50 max-h-64 overflow-x-hidden overflow-y-auto ring-1 ring-slate-200/50">
    {isLoadingAirports ? (
      <div className="px-4 py-4 text-center text-slate-500 text-sm">
        <FaSpinner className="animate-spin mx-auto mb-2 text-slate-400" />
        Searching airports...
      </div>
    ) : airports.length > 0 ? (
      airports.map((location, index) => (
        <button
          type="button"
          key={location.code || index}
          className="w-full text-left px-4 py-3 hover:bg-blue-50 active:bg-blue-100/80 cursor-pointer text-sm text-gray-700 border-b border-slate-100 last:border-b-0 transition-colors"
          onClick={() => {
            const displayValue = `${location.name} (${location.code})`;
            onSelect(displayValue);
            onClose();
          }}
        >
          <div className="font-medium text-slate-800">
            {location.name} ({location.code})
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            {location.city}, {location.country}
          </div>
        </button>
      ))
    ) : (
      <div className="px-4 py-4 text-center text-slate-500 text-sm">
        {isLoadingLocations
          ? "Loading airports..."
          : "Type city, airport name or code (min 2 characters) to search"}
      </div>
    )}
  </div>
);

const SwapButton = ({ onSwap }) => (
  <motion.div
    className="flex justify-center items-end pb-1 md:pb-0 md:items-center w-full md:w-auto"
    initial={{ opacity: 0, scale: 0 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.5, delay: 0.8 }}
  >
    <motion.button
      type="button"
      onClick={onSwap}
      aria-label="Swap origin and destination"
      className="w-11 h-11 min-w-[44px] min-h-[44px] bg-slate-100 hover:bg-slate-200 rounded-full flex items-center justify-center transition-colors duration-200 border border-slate-200/80 shadow-sm"
      whileHover={{ scale: 1.08, rotate: 180 }}
      whileTap={{ scale: 0.95 }}
    >
      <FaExchangeAlt className="text-slate-600 text-base" />
    </motion.button>
  </motion.div>
);

const DateInputs = ({
  formData,
  tripType,
  handleInputChange,
  getTodayDate,
  getMinReturnDate,
}) => (
  <>
    <DateInput
      label="Departure Date"
      name="departure"
      value={formData.departure}
      onChange={handleInputChange}
      min={getTodayDate()}
      required
    />

    {tripType === 1 && (
      <DateInput
        label="Return Date"
        name="returnDate"
        value={formData.returnDate}
        onChange={handleInputChange}
        min={getMinReturnDate()}
        required
      />
    )}
  </>
);

const DateInput = ({ label, name, value, onChange, min, required }) => (
  <motion.div
    className="relative flex-1 w-full min-w-0"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: 1.0 }}
  >
    <label className="block text-sm font-semibold text-gray-700 mb-2">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <div className="relative">
      <FaCalendarAlt className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 text-lg pointer-events-none" />
      <input
        type="date"
        name={name}
        value={value}
        onChange={onChange}
        min={min}
        className="w-full pl-11 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400 transition-all duration-200 bg-white text-gray-800 hover:border-slate-300"
        required={required}
      />
    </div>
  </motion.div>
);

const SearchButton = ({ isLoading, setWaiting }) => (
  <motion.div
    className="flex justify-center sm:justify-end mt-6 pt-2"
    initial={{ opacity: 0, x: 50 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ duration: 0.5, delay: 1.1 }}
  >
    <motion.button
      type="submit"
      disabled={isLoading}
      onClick={() => {
        setWaiting();
        setTimeout(() => {
          const start = window.scrollY;
          const distance = 400;
          const duration = 600;
          const startTime = performance.now();
          const easeInOutQuad = (t) =>
            t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
          const animateScroll = (currentTime) => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const ease = easeInOutQuad(progress);
            window.scrollTo(0, start + distance * ease);
            if (elapsed < duration) requestAnimationFrame(animateScroll);
          };
          requestAnimationFrame(animateScroll);
        }, 300);
      }}
      className={`min-h-[52px] w-full sm:w-auto min-w-[200px] bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-8 py-3.5 rounded-xl font-semibold flex items-center justify-center gap-3 transition-all duration-200 shadow-lg shadow-blue-500/30 hover:shadow-xl hover:shadow-blue-500/40 focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 ${
        isLoading ? "opacity-80 cursor-not-allowed" : ""
      }`}
      whileHover={!isLoading ? { scale: 1.02 } : {}}
      whileTap={!isLoading ? { scale: 0.98 } : {}}
    >
      {isLoading ? (
        <>
          <FaSpinner className="text-lg animate-spin" />
          <span>Searching Flights...</span>
        </>
      ) : (
        <>
          <span>Find Flights</span>
          <FaSearch className="text-lg" />
        </>
      )}
    </motion.button>
  </motion.div>
);

export default SearchForm;